import { phraseTempsPasse } from '../../lib/tempsMission';
import { montantRemiseExtra, lignesRemise } from '../../lib/remiseExtras';
import { RemiseExtraField } from '../PrestationPlanning/RemiseExtraField';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Clock, Phone, Mail, MapPin, CheckCircle, PlayCircle, Loader2, X, Plus, Trash2, User, Navigation, MessageSquare, Send, Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { SendCustomMessageModal } from '../ui/SendCustomMessageModal';
import { PhotoCaptureField } from '../ui/PhotoCaptureField';
import { ConsignesPhotoAvantApres } from '../ui/ConsignesPhotoAvantApres';
import { PhotosEnsembleField, type PhotoEnsemble } from '../ui/PhotosEnsembleField';
import { useCloturePolitique, exigeAvantApres, avecAvantApres, modeEnsemble, avecPaires, pairesObligatoires, type CloturePolitique } from '../../lib/cloturePolitique';
import { PhotosParPaires, verifierPaires } from '../ui/PhotosParPaires';
import { SignaturePad, type SignaturePadHandle } from '../ui/SignaturePad';
import {
    WeeklyHours, DAY_LABELS, defaultWeeklyHours, PX_PER_HOUR, timeToMinutes, minutesToTime,
    formatDateInput, formatDateHuman, contactLabel, dayKeyForDate, dayShortLabel, packAppointments,
    EmailLogEntry, EMAIL_TYPE_LABELS,
} from '../PrestationPlanning/prestationPlanningShared';

const api = (path: string, options?: RequestInit) => {
    const token = localStorage.getItem('token');
    return fetch(`/api${path}`, {
        ...options,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options?.headers }
    }).then((r) => r.json());
};

const fmt = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);

interface CatalogItem { id: string; label: string; unit_price_ttc: number; tax_rate: number; show_tva?: boolean; }
interface ExtraLine { label: string; unit_price_ttc: number; tax_rate: number; quantity: number; formula_note?: string; discount_type?: 'percent' | 'amount' | 'offered' | null; discount_value?: number; offered_quantity?: number; }
interface FormulaBenefitItem { catalog_item_id: string; benefit: { free: boolean; discount_type: string; discount_value: number }; free_occurrence_numbers: number[]; }
interface Employee { id: string; first_name: string; last_name: string; }

interface Appointment {
    id: string;
    crm_first_name?: string;
    crm_last_name?: string;
    crm_company_name?: string;
    crm_phone?: string;
    crm_email?: string;
    crm_address?: string;
    crm_postal_code?: string;
    crm_city?: string;
    location_type?: 'domicile' | 'entreprise';
    // Vrai quand la direction masque les prix au salarié : aucun montant, ni encaissement, ni facture.
    prices_hidden?: boolean;
    // Qui peut commencer / terminer et quelles photos (calculé par le serveur selon le type de client).
    cloture_politique?: CloturePolitique;
    employee_ids: string[];
    prestation_label: string;
    quantity: number;
    unit_price_ttc: number;
    tax_rate: number;
    catalog_items?: { catalog_item_id: string | null; label: string; quantity: number; unit_price_ttc: number; tax_rate: number; unit_duration_minutes: number }[];
    computed_duration_minutes: number;
    appointment_date: string;
    start_time: string;
    status: string;
    payment_status: string;
    payment_method?: string;
    payment_breakdown?: { method: string; amount: number }[];
    completed_at?: string;
    started_at?: string;
    photo_before_url?: string;
    total_amount_ttc?: number;
    amount_already_paid?: number;
    facture_id?: string;
    formula_occurrence_number?: number | null;
    notes: string;
}

const employeeNamesLabel = (appt: Appointment, employees: Employee[]) => {
    return (appt.employee_ids || [])
        .map((id) => {
            const e = employees.find((x) => x.id === id);
            return e ? `${e.first_name} ${e.last_name}`.trim() : '';
        })
        .filter(Boolean)
        .join(', ');
};

const pillClass = (active: boolean) =>
    `flex-1 px-4 py-2.5 rounded-xl text-sm font-bold border transition-all ${active ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`;

function DetailModal({ appt, onClose, onStartCompletion, onStartPrestation }: { appt: Appointment; onClose: () => void; onStartCompletion: () => void; onStartPrestation: () => void; }) {
    const [tab, setTab] = useState<'details' | 'emails'>('details');
    const [emails, setEmails] = useState<EmailLogEntry[]>([]);
    const [loadingEmails, setLoadingEmails] = useState(false);
    const [resending, setResending] = useState(false);
    const [resendMessage, setResendMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [sendingReminder, setSendingReminder] = useState(false);
    const [showSendMessage, setShowSendMessage] = useState(false);
    const [reminderMessage, setReminderMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [previewEmail, setPreviewEmail] = useState<EmailLogEntry | null>(null);
    const [resendingEmailId, setResendingEmailId] = useState<string | null>(null);
    const [resendEmailResult, setResendEmailResult] = useState<{ id: string; ok: boolean; msg: string } | null>(null);

    const endTime = minutesToTime(timeToMinutes(appt.start_time) + Math.round(appt.computed_duration_minutes));
    const fullAddress = [appt.crm_address, [appt.crm_postal_code, appt.crm_city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    const mapsUrl = fullAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}` : null;
    const isClosed = appt.status === 'termine';
    const remainingDue = Math.max(0, Number(appt.total_amount_ttc || 0) - Number(appt.amount_already_paid || 0));
    // Une fois clôturée ET payée intégralement, plus rien à notifier — la "date passée" n'est plus le
    // critère : une prestation clôturée mais pas encore payée en intégralité doit au contraire pouvoir
    // être relancée pour le paiement, peu importe la date.
    const isFullyPaid = isClosed && Number(appt.total_amount_ttc || 0) > 0 && remainingDue <= 0;
    const isClosedUnpaid = isClosed && remainingDue > 0;
    const fmt = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

    const loadEmails = () => {
        setLoadingEmails(true);
        api(`/employe/prestation-planning/appointments/${appt.id}/emails`)
            .then((d) => setEmails(Array.isArray(d) ? d : []))
            .finally(() => setLoadingEmails(false));
    };

    useEffect(() => {
        if (tab !== 'emails') return;
        loadEmails();
    }, [tab]);

    const handleResend = async () => {
        setResending(true);
        setResendMessage(null);
        try {
            const res: any = await api(`/employe/prestation-planning/appointments/${appt.id}/resend-confirmation`, { method: 'POST' });
            if (res?.success) {
                setResendMessage({ type: 'success', text: 'Email envoyé.' });
                if (tab === 'emails') loadEmails();
            } else {
                setResendMessage({ type: 'error', text: res?.error || 'Erreur lors de l\'envoi.' });
            }
        } catch {
            setResendMessage({ type: 'error', text: 'Erreur lors de l\'envoi.' });
        } finally {
            setResending(false);
        }
    };

    const handleResendEmail = async (emailId: string) => {
        setResendingEmailId(emailId);
        setResendEmailResult(null);
        try {
            const res: any = await api(`/employe/prestation-planning/appointments/${appt.id}/emails/${emailId}/resend`, { method: 'POST' });
            if (res?.success) {
                setResendEmailResult({ id: emailId, ok: true, msg: 'Envoyé.' });
                loadEmails();
            } else {
                setResendEmailResult({ id: emailId, ok: false, msg: res?.error || 'Erreur lors de l\'envoi.' });
            }
        } catch {
            setResendEmailResult({ id: emailId, ok: false, msg: 'Erreur lors de l\'envoi.' });
        } finally {
            setResendingEmailId(null);
        }
    };

    const handleSendReminder = async () => {
        setSendingReminder(true);
        setReminderMessage(null);
        try {
            const res: any = await api(`/employe/prestation-planning/appointments/${appt.id}/send-reminder`, { method: 'POST' });
            if (res?.success) {
                setReminderMessage({ type: 'success', text: res?.warning || 'Rappel envoyé.' });
                if (tab === 'emails') loadEmails();
            } else {
                setReminderMessage({ type: 'error', text: res?.error || 'Erreur lors de l\'envoi.' });
            }
        } catch {
            setReminderMessage({ type: 'error', text: 'Erreur lors de l\'envoi.' });
        } finally {
            setSendingReminder(false);
        }
    };

    // "Relancer pour le paiement" — relance le client sur le solde restant d'une prestation déjà
    // clôturée (réutilise le même email facture, dont le corps s'adapte déjà au statut de paiement —
    // reste à payer/échéance — plutôt que d'inventer un nouveau type de message).
    const handleSendPaymentReminder = async () => {
        if (!appt.crm_email) {
            setReminderMessage({ type: 'error', text: 'Aucune adresse email renseignée pour ce client.' });
            return;
        }
        setSendingReminder(true);
        setReminderMessage(null);
        try {
            const invoice: any = await api(`/employe/prestation-planning/appointments/${appt.id}/invoice`);
            if (invoice?.error) { setReminderMessage({ type: 'error', text: invoice.error }); return; }
            const payload = invoice.payload_json || {};
            const snapshot = invoice.billing_snapshot || {};
            const { generateInvoicePdfFromData } = await import('../Factures/invoicePdfGenerator');
            const { pdf } = await generateInvoicePdfFromData({ invoice, payload, snapshot });
            const dataUri: string = pdf.output('datauristring');
            const pdfBase64 = dataUri.split(',')[1];
            const res: any = await api(`/employe/prestation-planning/appointments/${appt.id}/send-invoice-email`, {
                method: 'POST',
                body: JSON.stringify({ to: appt.crm_email, pdfBase64, filename: `${invoice.invoice_number || 'facture'}.pdf` }),
            });
            if (res?.success) {
                setReminderMessage({ type: 'success', text: 'Relance envoyée.' });
                if (tab === 'emails') loadEmails();
            } else if (res?.skipped) {
                // Pas soldé : aucune facture ne part, c'est la logique du site — rien à signaler.
            } else {
                setReminderMessage({ type: 'error', text: res?.error || 'Erreur lors de l\'envoi.' });
            }
        } catch {
            setReminderMessage({ type: 'error', text: 'Erreur lors de l\'envoi.' });
        } finally {
            setSendingReminder(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-md bg-surface border border-border rounded-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-2xl bg-brand-500/15 flex items-center justify-center text-brand-500 shrink-0">
                            <User size={18} />
                        </div>
                        <div className="min-w-0">
                            <h3 className="font-bold text-content truncate">{contactLabel(appt)}</h3>
                            {appt.status === 'en_cours' && (
                                <span className="inline-block mt-0.5 text-[10px] font-black uppercase bg-brand-500/10 text-brand-500 border border-brand-500/20 px-1.5 py-0.5 rounded">En cours</span>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-content-muted hover:text-content rounded-xl hover:bg-surface-soft shrink-0"><X size={18} /></button>
                </div>

                <div className="flex gap-1 bg-surface-soft border-b border-border p-2 shrink-0">
                    <button onClick={() => setTab('details')} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${tab === 'details' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                        Détails
                    </button>
                    <button onClick={() => setTab('emails')} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${tab === 'emails' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                        <MessageSquare size={12} /> Historique message
                    </button>
                </div>

                {tab === 'details' ? (
                    <div className="p-5 space-y-4 overflow-y-auto">
                        <div className="p-3 rounded-xl bg-surface-soft border border-border flex items-center justify-between">
                            <span className="text-xs text-content-muted flex items-center gap-1.5"><Clock size={13} /> Horaire</span>
                            <span className="font-bold text-sm text-content">{appt.start_time} - {endTime}</span>
                        </div>
                        <div className="space-y-1">
                            <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Prestations</h4>
                            {(appt.catalog_items && appt.catalog_items.length > 0 ? appt.catalog_items : [{ label: appt.prestation_label, quantity: appt.quantity }]).map((item, idx) => (
                                <p key={idx} className="text-sm text-content">{item.label}{item.quantity > 1 ? ` × ${item.quantity}` : ''}</p>
                            ))}
                        </div>
                        <div className="space-y-1.5 text-sm text-content">
                            {/* « Appeler » (tel:) et « Écrire » (mailto:) : le téléphone ouvre l'appel, ou propose le choix de l'application de messagerie. */}
                            {appt.crm_phone && (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="flex items-center gap-2"><Phone size={13} className="text-content-muted shrink-0" /> {appt.crm_phone}</p>
                                    <a href={`tel:${String(appt.crm_phone).replace(/[^\d+]/g, '')}`}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all">
                                        <Phone size={12} /> Appeler
                                    </a>
                                </div>
                            )}
                            {appt.crm_email && (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="flex items-center gap-2"><Mail size={13} className="text-content-muted shrink-0" /> {appt.crm_email}</p>
                                    <a href={`mailto:${appt.crm_email}`}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all">
                                        <Mail size={12} /> Écrire
                                    </a>
                                </div>
                            )}
                        </div>
                        <span className="inline-block w-fit text-[10px] font-black uppercase px-2 py-0.5 rounded-lg bg-surface-soft border border-border text-content-muted">
                            {appt.location_type === 'entreprise' ? "Dans l'entreprise" : 'Chez le client'}
                        </span>
                        {fullAddress && (
                            <div className="space-y-2">
                                <p className="flex items-start gap-2 text-sm text-content"><MapPin size={13} className="text-content-muted shrink-0 mt-0.5" /> {fullAddress}</p>
                                {mapsUrl && (
                                    <a href={mapsUrl} target="_blank" rel="noreferrer"
                                        className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-sm font-bold transition-all">
                                        <Navigation size={14} /> Ouvrir sur Maps
                                    </a>
                                )}
                            </div>
                        )}
                        {appt.notes && <p className="text-xs text-content-muted italic whitespace-pre-wrap">{appt.notes}</p>}
                        {appt.status === 'termine' && !appt.prices_hidden && (
                            <div className="space-y-2">
                                <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Paiement</h4>
                                <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1.5 text-sm">
                                    {(appt.payment_breakdown && appt.payment_breakdown.length > 0
                                        ? appt.payment_breakdown
                                        : (appt.payment_method ? [{ method: appt.payment_method, amount: Number(appt.amount_already_paid || 0) }] : [])
                                    ).map((entry, idx) => (
                                        <div key={idx} className="flex items-center justify-between">
                                            <span className="text-content-muted">{entry.method}</span>
                                            <span className="font-bold text-content">{fmt(entry.amount)}</span>
                                        </div>
                                    ))}
                                    {!appt.payment_method && (!appt.payment_breakdown || appt.payment_breakdown.length === 0) && (
                                        <p className="text-content-muted italic text-xs">Aucun règlement enregistré.</p>
                                    )}
                                    <div className="pt-1.5 border-t border-border flex items-center justify-between">
                                        <span className="text-content-muted">{isFullyPaid ? 'Total réglé' : 'Réglé à ce jour'}</span>
                                        <span className={`font-black ${isFullyPaid ? 'text-emerald-400' : 'text-content'}`}>{fmt(Number(appt.amount_already_paid || 0))}</span>
                                    </div>
                                    {phraseTempsPasse(appt.started_at, appt.completed_at, appt.computed_duration_minutes) && (
                                        <p className="text-[11px] text-content-muted pt-0.5">{phraseTempsPasse(appt.started_at, appt.completed_at, appt.computed_duration_minutes)}</p>
                                    )}
                                    {appt.completed_at && (
                                        <p className="text-[11px] text-content-muted pt-0.5">
                                            Clôturé le {new Date(appt.completed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                        {!isClosed && (
                            <button onClick={handleResend} disabled={resending}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                                <Send size={13} /> {resending ? 'Envoi...' : 'Renvoyer l\'email du rendez-vous au client'}
                            </button>
                        )}
                        {appt.crm_email && (
                            <button onClick={() => setShowSendMessage(true)}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all">
                                <Mail size={13} /> Message personnalisé
                            </button>
                        )}
                        {resendMessage && (
                            <p className={`text-xs font-semibold text-center ${resendMessage.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>{resendMessage.text}</p>
                        )}
                        {!isFullyPaid && (
                            <button onClick={isClosedUnpaid ? handleSendPaymentReminder : handleSendReminder} disabled={sendingReminder}
                                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all ${isClosedUnpaid ? 'border-warning-500/30 bg-warning-500/10 text-warning-400 hover:bg-warning-500/20' : 'border-border text-content-muted hover:text-content hover:bg-surface-soft'}`}>
                                <Bell size={13} /> {sendingReminder ? 'Envoi...' : (isClosedUnpaid ? 'Relancer pour le paiement' : 'Envoyer un rappel de rendez-vous')}
                            </button>
                        )}
                        {reminderMessage && (
                            <p className={`text-xs font-semibold text-center ${reminderMessage.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>{reminderMessage.text}</p>
                        )}
                    </div>
                ) : (
                    <div className="p-5 space-y-2 overflow-y-auto">
                        {loadingEmails ? (
                            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={20} /></div>
                        ) : emails.length === 0 ? (
                            <p className="text-center text-sm text-content-muted italic py-8">Aucun email envoyé pour ce rendez-vous pour l'instant.</p>
                        ) : (
                            emails.map((e) => (
                                <div key={e.id} className="p-3 rounded-xl bg-surface-soft border border-border">
                                    <button onClick={() => setPreviewEmail(e)} className="w-full text-left">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-bold text-content">{EMAIL_TYPE_LABELS[e.email_type] || e.subject}</span>
                                            <span className="text-[10px] text-content-muted shrink-0 ml-2">{new Date(e.sent_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                        </div>
                                        <p className="text-[11px] text-content-muted mt-0.5">Envoyé à {e.recipient_email}</p>
                                    </button>
                                    <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-border">
                                        <button onClick={() => handleResendEmail(e.id)} disabled={resendingEmailId === e.id}
                                            className="flex items-center gap-1 text-[11px] font-bold text-content-muted hover:text-content disabled:opacity-50 transition-all">
                                            <Send size={10} /> {resendingEmailId === e.id ? 'Envoi...' : 'Renvoyer'}
                                        </button>
                                        {resendEmailResult?.id === e.id && (
                                            <span className={`text-[11px] font-semibold ${resendEmailResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>{resendEmailResult.msg}</span>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                <div className="p-5 border-t border-border shrink-0 space-y-2">
                    {appt.status !== 'termine' && appt.cloture_politique && !appt.cloture_politique.peut_cloturer && (
                        <p className="text-xs text-content-muted text-center rounded-xl bg-surface-soft border border-border px-3 py-2.5">
                            {appt.cloture_politique.role_label || 'Responsable'} : <b className="text-content">{appt.cloture_politique.responsable_nom || 'la personne désignée'}</b>. Seul(e) cette personne commence et termine cette prestation (photos et signature du client).
                        </p>
                    )}
                    {appt.status === 'planifie' && !(appt.cloture_politique && !appt.cloture_politique.peut_cloturer) && (
                        <button onClick={onStartPrestation} className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-brand-500 text-brand-500 hover:bg-brand-500/10 font-bold text-sm transition-all">
                            <PlayCircle size={16} /> Commencer la prestation
                        </button>
                    )}
                    {appt.status !== 'termine' && (appt.cloture_politique && !appt.cloture_politique.peut_cloturer) ? null : appt.status !== 'termine' ? (
                        <button onClick={onStartCompletion} className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all">
                            <CheckCircle size={16} /> Terminer la prestation
                        </button>
                    ) : (
                        <button onClick={onClose} className="w-full px-5 py-3 rounded-xl border border-border text-content-muted hover:text-content font-bold text-sm">Fermer</button>
                    )}
                </div>
            </motion.div>
            {showSendMessage && (
                <SendCustomMessageModal
                    recipientLabel={contactLabel(appt)}
                    tokens={[
                        { token: '{{prenom}}', desc: 'Prénom du client' },
                        { token: '{{nom}}', desc: 'Nom (ou raison sociale) du client' },
                        { token: '{{date}}', desc: 'Date du rendez-vous' },
                        { token: '{{heure}}', desc: 'Heure du rendez-vous' },
                        { token: '{{entreprise}}', desc: 'Votre établissement' },
                        { token: '{{logo}}', desc: 'Votre logo' },
                    ]}
                    onClose={() => setShowSendMessage(false)}
                    onSend={async (subject, body) => {
                        const res = await api(`/employe/prestation-planning/appointments/${appt.id}/send-message`, {
                            method: 'POST',
                            body: JSON.stringify({ subject, body }),
                        });
                        if (res?.success && tab === 'emails') loadEmails();
                        return res;
                    }}
                />
            )}
            {previewEmail && (
                <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[80] flex items-center justify-center p-4">
                    <div className="w-full max-w-lg bg-surface border border-border rounded-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                            <div className="min-w-0">
                                <h3 className="font-bold text-content text-sm truncate">{previewEmail.subject || EMAIL_TYPE_LABELS[previewEmail.email_type] || 'Email'}</h3>
                                <p className="text-xs text-content-muted mt-0.5 truncate">Envoyé à {previewEmail.recipient_email} le {new Date(previewEmail.sent_at).toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                            <button onClick={() => setPreviewEmail(null)} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft shrink-0"><X size={18} /></button>
                        </div>
                        <div className="p-5 overflow-y-auto bg-surface-soft">
                            {previewEmail.html_body ? (
                                <div className="bg-white rounded-xl overflow-hidden border border-border">
                                    <iframe srcDoc={previewEmail.html_body} className="w-full" style={{ height: '55vh', border: 'none' }} sandbox="" title="Aperçu de l'email" />
                                </div>
                            ) : (
                                <p className="text-xs text-content-muted italic">Le contenu de cet email n'a pas été conservé (envoyé avant l'ajout de cet aperçu) — impossible de l'afficher ou de le renvoyer tel quel.</p>
                            )}
                        </div>
                        <div className="flex justify-between items-center gap-3 p-5 border-t border-border shrink-0">
                            {resendEmailResult?.id === previewEmail.id && (
                                <span className={`text-xs font-semibold ${resendEmailResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>{resendEmailResult.msg}</span>
                            )}
                            <div className="flex gap-3 ml-auto">
                                <button onClick={() => setPreviewEmail(null)} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-sm font-bold">Fermer</button>
                                {previewEmail.html_body && (
                                    <button onClick={() => handleResendEmail(previewEmail.id)} disabled={resendingEmailId === previewEmail.id}
                                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                                        <Send size={14} /> {resendingEmailId === previewEmail.id ? 'Envoi...' : 'Renvoyer cet email'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// "Commencer la prestation" (2026-09-24, demande d'Emeni) : la photo "avant" se prend à l'arrivée chez le
// client, pas reconstituée de mémoire à la clôture. Facultatif — fermer cette fenêtre et aller directement
// sur "Terminer la prestation" redemande les deux photos d'un coup, comme avant cette fonctionnalité.
function StartWizard({ appt, onClose, onDone }: { appt: Appointment; onClose: () => void; onDone: () => void; }) {
    const [photoBefore, setPhotoBefore] = useState<string | null>(null);
    const [photosRequises, setPhotosRequises] = useState(true);
    const politique = useCloturePolitique(appt.id, (u: string) => api(u), appt.cloture_politique || null);
    // Tant que la politique n'est pas connue : comportement historique (jamais de blocage à tort).
    const exigeAvant = politique ? exigeAvantApres(politique.photos_mode) : photosRequises;
    const afficherPhoto = politique ? avecAvantApres(politique.photos_mode) : true;
    const bloque = !!politique && !politique.peut_cloturer;
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        api('/employe/prestation-planning/settings')
            .then((s: any) => setPhotosRequises(s?.completion_photos_required !== false))
            .catch(() => {});
    }, []);

    // Photos par type d'article (2026-10-20) : envoyées une à une par PhotosParPaires ; le serveur vérifie l'obligation.
    const parPaires = politique ? avecPaires(politique.photos_mode) : false;

    const handleValidate = async () => {
        if (bloque) { setError(`Seul(e) ${politique?.responsable_nom || 'la personne désignée'} peut commencer cette prestation.`); return; }
        if (!parPaires && exigeAvant && !photoBefore) { setError('La photo avant intervention est obligatoire.'); return; }
        setSubmitting(true);
        setError('');
        try {
            const res: any = await api(`/employe/prestation-planning/appointments/${appt.id}/commencer`, {
                method: 'POST',
                body: JSON.stringify(parPaires ? { photos_paires: true } : { photo_before: photoBefore }),
            });
            if (res?.error) { setError(res.error); setSubmitting(false); return; }
            onDone();
        } catch (e) {
            setError('Erreur lors du démarrage de la prestation.');
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-md bg-surface border border-border rounded-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                    <h3 className="font-bold text-content flex items-center gap-2"><PlayCircle size={18} className="text-brand-500" /> Commencer la prestation</h3>
                    <button onClick={onClose} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4 overflow-y-auto">
                    {!(parPaires && !bloque) && <p className="text-xs text-content-muted">
                        {bloque
                            ? `Seul(e) ${politique?.responsable_nom || 'la personne désignée'} commence et termine cette prestation.`
                            : !afficherPhoto
                            ? "Aucune photo n'est demandée au démarrage. Les photos d'ensemble se prennent à la fin de la prestation."
                            : exigeAvant
                            ? 'Prenez la photo avant intervention maintenant : elle servira de référence pour la photo après, prise au même endroit, au même angle, à la même lumière.'
                            : 'Photo facultative — si vous la prenez maintenant, elle servira de référence pour la photo après, au même endroit et au même angle.'}
                    </p>}
                    {parPaires && !bloque && politique && (
                        <PhotosParPaires appointmentId={appt.id} etape="demarrer" appel={api} obligatoire={pairesObligatoires(politique.photos_mode)} />
                    )}
                    {!parPaires && afficherPhoto && !bloque && <ConsignesPhotoAvantApres />}
                    {!parPaires && afficherPhoto && !bloque && <PhotoCaptureField label="Photo avant" value={photoBefore} onChange={setPhotoBefore} />}
                    {error && <p className="text-xs text-red-400 font-semibold">{error}</p>}
                </div>
                <div className="flex justify-end gap-3 p-5 border-t border-border shrink-0">
                    <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-sm font-bold">Annuler</button>
                    <button onClick={handleValidate} disabled={submitting || bloque} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                        {submitting ? 'Démarrage...' : 'Commencer'}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}

function CompletionWizard({ appt, onClose, onDone }: { appt: Appointment; onClose: () => void; onDone: () => void; }) {
    // Étape 0 (constat de clôture, obligatoire côté serveur depuis 2026-09-05) : ce wizard ne la
    // proposait jamais, d'où le blocage « il manque des photos » à la validation — le salarié n'avait
    // simplement nulle part où les prendre. Même schéma que AdminCompletionModal, côté établissement.
    const [step, setStep] = useState<0 | 1 | 2 | 3>(0);
    // Rapport fait/pas fait (2026-09-25, demande d'Emeni) : purement informatif — sert a decider s'il
    // faut planifier un rendez-vous de rattrapage, ne change jamais le montant facture ici.
    const lignesPrevues = useMemo<{ catalogItemId: string | null; label: string; quantityPlanned: number }[]>(() => {
        if (Array.isArray(appt.catalog_items) && appt.catalog_items.length > 0) {
            return appt.catalog_items.map((it) => ({ catalogItemId: it.catalog_item_id || null, label: it.label, quantityPlanned: Math.max(1, Math.round(Number(it.quantity) || 1)) }));
        }
        return [{ catalogItemId: null, label: appt.prestation_label, quantityPlanned: Math.max(1, Math.round(Number(appt.quantity) || 1)) }];
    }, [appt]);
    const [quantitesFaites, setQuantitesFaites] = useState<number[]>(() => lignesPrevues.map((l) => l.quantityPlanned));
    // Deja capturee via "Commencer la prestation" (2026-09-24) : jamais redemandee ici, juste rappelee.
    const dejaCommencee = appt.status === 'en_cours' && !!appt.photo_before_url;
    const [photoBefore, setPhotoBefore] = useState<string | null>(dejaCommencee ? appt.photo_before_url! : null);
    const [photoAfter, setPhotoAfter] = useState<string | null>(null);
    const [comment, setComment] = useState('');
    const [signataireNom, setSignataireNom] = useState('');
    const signaturePadRef = React.useRef<SignaturePadHandle>(null);
    // Capturée dès qu'on quitte l'étape 0, jamais relue depuis la ref à la validation : le <SignaturePad>
    // n'existe QUE pendant l'étape 0 (rendu conditionnel par étape) — une fois qu'on avance, il est démonté
    // et sa ref redevient null. La relire à l'étape 2 perdait donc silencieusement la signature à chaque
    // clôture (même bug que côté établissement, cf. AdminCompletionModal).
    const [clientSignature, setClientSignature] = useState<string | null>(null);
    const [step0Error, setStep0Error] = useState('');
    const [photosRequises, setPhotosRequises] = useState(true);
    useEffect(() => {
        api('/employe/prestation-planning/settings')
            .then((s: any) => setPhotosRequises(s?.completion_photos_required !== false))
            .catch(() => {});
    }, []);
    // Politique de clôture (responsable sur place, photos d'ensemble pour les entreprises) : réglée par l'établissement.
    const politique = useCloturePolitique(appt.id, (u: string) => api(u), appt.cloture_politique || null);
    const modeP = politique?.photos_mode || (photosRequises ? 'avant_apres' : 'facultatives');
    const exigeAvant = exigeAvantApres(modeP);
    const afficherAvantApres = avecAvantApres(modeP);
    const ensemble = modeEnsemble(modeP);
    const [photosEnsemble, setPhotosEnsemble] = useState<PhotoEnsemble[]>([]);
    const bloque = !!politique && !politique.peut_cloturer;
    // Photos par type d'article (2026-10-20). Un rendez-vous démarré AVANT cette version avec l'ancienne photo
    // unique (dejaCommencee) se termine à l'ancienne, pour ne pas perdre sa photo avant.
    const parPaires = !!politique && avecPaires(modeP) && !dejaCommencee;
    const manquePhotos = !parPaires && ((exigeAvant && (!photoBefore || !photoAfter)) || (modeP === 'ensemble' && photosEnsemble.length === 0 && !photoBefore && !photoAfter));
    const messagePhotos = exigeAvant ? 'Les deux photos (avant/après) sont obligatoires.' : "Ajoutez au moins une photo d'ensemble.";
    const [hasExtras, setHasExtras] = useState<boolean | null>(null);
    const [extras, setExtras] = useState<ExtraLine[]>([]);
    const [catalog, setCatalog] = useState<CatalogItem[]>([]);
    const [tvaRates, setTvaRates] = useState<number[]>([20]);
    const [paymentStatus, setPaymentStatus] = useState<'encaisse' | 'a_encaisser'>('a_encaisser');
    const [paymentEntries, setPaymentEntries] = useState<{ method: string; amount: number }[]>([{ method: '', amount: 0 }]);
    // Ce qui a déjà été réglé en ligne avant le rendez-vous (acompte + frais kilométriques éventuels) —
    // automatiquement déduit du reste à payer, jamais ressaisi. Distinct du paiement éventuellement encaissé
    // aujourd'hui (champ ci-dessous) pour que l'historique des paiements ne confonde jamais les deux.
    const alreadyPaidOnline = Math.max(0, Number(appt.amount_already_paid) || 0);
    const [newPaymentAmount, setNewPaymentAmount] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        api('/employe/prestation-catalog').then((d: any) => {
            setCatalog(Array.isArray(d?.catalog) ? d.catalog : []);
            setTvaRates(Array.isArray(d?.tva_rates) && d.tva_rates.length > 0 ? d.tva_rates : [20]);
        });
    }, []);

    // Ce que la formule du client (le cas échéant) applique à ce rendez-vous précis — pour avertir
    // immédiatement le salarié quand un extra ajouté est offert, réduit, ou n'est plus offert à ce passage
    // (demande d'Emeni, 2026-09-03).
    const [formulaBenefits, setFormulaBenefits] = useState<{ formula_name: string | null; occurrence_number: number | null; items: FormulaBenefitItem[] }>({ formula_name: null, occurrence_number: null, items: [] });
    useEffect(() => {
        api(`/prestation-planning/appointments/${appt.id}/formula-benefits`).then((r: any) => { if (r && !r.error) setFormulaBenefits(r); });
    }, [appt.id]);

    const addCatalogExtra = (item: CatalogItem) => {
        const taxRate = item.show_tva !== false ? item.tax_rate : 0;
        let unitPrice = item.unit_price_ttc;
        let formulaNote: string | undefined;
        const fb = formulaBenefits.items.find((it) => it.catalog_item_id === item.id);
        if (fb) {
            if (fb.benefit.free) {
                unitPrice = 0;
                formulaNote = `Offert par la formule « ${formulaBenefits.formula_name} » (passage ${formulaBenefits.occurrence_number})`;
            } else if (fb.benefit.discount_type === 'percent' && fb.benefit.discount_value > 0) {
                unitPrice = Math.round(unitPrice * (1 - fb.benefit.discount_value / 100) * 100) / 100;
                formulaNote = `Réduction formule « ${formulaBenefits.formula_name} » : -${fb.benefit.discount_value}%`;
            } else if (fb.benefit.discount_type === 'amount' && fb.benefit.discount_value > 0) {
                unitPrice = Math.max(0, Math.round((unitPrice - fb.benefit.discount_value) * 100) / 100);
                formulaNote = `Réduction formule « ${formulaBenefits.formula_name} » : -${fb.benefit.discount_value}€`;
            } else if (fb.free_occurrence_numbers.length > 0) {
                formulaNote = `Le client n'a plus d'occurrence offerte pour cette prestation (offerte au${fb.free_occurrence_numbers.length > 1 ? 'x' : ''} passage${fb.free_occurrence_numbers.length > 1 ? 's' : ''} ${fb.free_occurrence_numbers.join(', ')}) — facturée normalement.`;
            }
        }
        setExtras((prev) => [...prev, { label: item.label, unit_price_ttc: unitPrice, tax_rate: taxRate, quantity: 1, formula_note: formulaNote }]);
    };
    const addBlankExtra = () => setExtras((prev) => [...prev, { label: '', unit_price_ttc: 0, tax_rate: tvaRates[0] ?? 20, quantity: 1 }]);
    const updateExtra = (idx: number, patch: Partial<ExtraLine>) => setExtras((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
    const removeExtra = (idx: number) => setExtras((prev) => prev.filter((_, i) => i !== idx));

    const baseTotal = appt.unit_price_ttc * appt.quantity;
    const validExtras = extras.filter((e) => e.label.trim());
    const extrasTotal = validExtras.reduce((sum, e) => sum + e.unit_price_ttc * e.quantity - montantRemiseExtra(e), 0);
    const grandTotal = baseTotal + extrasTotal;
    const remainingAfterOnline = Math.max(0, grandTotal - alreadyPaidOnline);
    const newMoneyToday = paymentStatus === 'encaisse' ? remainingAfterOnline : Math.min(remainingAfterOnline, Math.max(0, Number(newPaymentAmount || 0)));
    const alreadyPaidNum = Math.min(grandTotal, alreadyPaidOnline + newMoneyToday);
    const remainingDue = Math.max(0, grandTotal - alreadyPaidNum);
    const paymentMethodRequired = newMoneyToday > 0;
    const amountToAllocate = newMoneyToday;

    // Tant qu'un seul moyen de paiement est utilisé, son montant reste synchronisé automatiquement sur
    // le total à régler (le salarié n'a rien à saisir) — dès qu'un deuxième moyen est ajouté, chaque
    // montant devient éditable pour permettre une répartition (ex: 20€ espèces + le reste en virement).
    useEffect(() => {
        setPaymentEntries((prev) => prev.length <= 1 ? [{ method: prev[0]?.method || '', amount: amountToAllocate }] : prev);
    }, [amountToAllocate]);

    const updatePaymentEntry = (idx: number, patch: Partial<{ method: string; amount: number }>) =>
        setPaymentEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
    const addPaymentEntry = () => {
        const allocated = paymentEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
        setPaymentEntries((prev) => [...prev, { method: '', amount: Math.max(0, Math.round((amountToAllocate - allocated) * 100) / 100) }]);
    };
    const removePaymentEntry = (idx: number) => setPaymentEntries((prev) => prev.filter((_, i) => i !== idx));
    const paymentEntriesSum = paymentEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
    const paymentEntriesValid = !paymentMethodRequired || (
        paymentEntries.every((e) => e.method && Number(e.amount) > 0) &&
        Math.abs(paymentEntriesSum - amountToAllocate) < 0.01
    );

    const handleValidate = async (signatureOverride?: string) => {
        const signature = signatureOverride || clientSignature;
        setSubmitting(true);
        setError('');
        try {
            if (bloque) { setStep(0); setStep0Error(`Seul(e) ${politique?.responsable_nom || 'la personne désignée'} peut terminer cette prestation.`); setSubmitting(false); return; }
            if (manquePhotos || !signature || !signataireNom.trim()) {
                setStep(0);
                setStep0Error(manquePhotos ? `${messagePhotos} Le nom et la signature sont aussi obligatoires.` : 'Le nom et la signature sont obligatoires.');
                setSubmitting(false);
                return;
            }
            const res = await api(`/employe/prestation-planning/appointments/${appt.id}/complete`, {
                method: 'POST',
                body: JSON.stringify({
                    extras: hasExtras ? validExtras : [],
                    payment_status: paymentStatus,
                    payment_entries: paymentMethodRequired ? paymentEntries : [],
                    new_payment_amount: newMoneyToday,
                    ...(parPaires ? { photos_paires: true } : { photo_before: photoBefore, photo_after: photoAfter, photos: ensemble ? photosEnsemble : [] }),
                    comment,
                    client_signature: signature,
                    client_signature_name: signataireNom.trim(),
                    completion_report: lignesPrevues.map((l, i) => ({
                        label: l.label, catalog_item_id: l.catalogItemId,
                        quantity_planned: l.quantityPlanned, quantity_done: quantitesFaites[i],
                    })),
                }),
            });
            if (res?.error) { setError(res.error); setSubmitting(false); return; }

            // L'email de fin de prestation part quoi qu'il arrive (généré côté navigateur avec jsPDF, pas
            // disponible côté serveur) — la facture PDF n'est jointe que si le module Facture est actif pour
            // cet établissement, auquel cas "invoice" est renseigné.
            if (res?.recipient_email && !appt.prices_hidden) {
                try {
                    let pdfBase64: string | undefined, filename: string | undefined;
                    if (res.invoice) {
                        const { generateInvoicePdfFromData } = await import('../Factures/invoicePdfGenerator');
                        const { pdf } = await generateInvoicePdfFromData({ invoice: res.invoice, payload: res.payload, snapshot: res.snapshot });
                        const dataUri: string = pdf.output('datauristring');
                        pdfBase64 = dataUri.split(',')[1];
                        filename = `${res.invoice?.invoice_number || 'facture'}.pdf`;
                    }
                    await api(`/employe/prestation-planning/appointments/${appt.id}/send-invoice-email`, {
                        method: 'POST',
                        body: JSON.stringify({ to: res.recipient_email, pdfBase64, filename }),
                    });
                } catch (pdfErr) {
                    console.error('Failed to generate/send invoice email', pdfErr);
                }
            }

            onDone();
        } catch (e: any) {
            setError('Erreur lors de la clôture');
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-md bg-surface border border-border rounded-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                    <h3 className="font-bold text-content flex items-center gap-2"><CheckCircle size={18} className="text-emerald-400" /> Terminer la prestation</h3>
                    <button onClick={onClose} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4 overflow-y-auto">
                    {step === 0 ? (
                        <>
                            <p className="text-sm text-content font-semibold">Constat de la prestation</p>
                            {bloque && (
                                <p className="text-xs text-red-400 font-semibold rounded-xl bg-red-500/10 border border-red-500/30 px-3 py-2">
                                    Seul(e) {politique?.responsable_nom || 'la personne désignée'} peut terminer cette prestation.
                                </p>
                            )}
                            {parPaires && politique && (
                                <PhotosParPaires appointmentId={appt.id} etape="terminer" appel={api} obligatoire={pairesObligatoires(modeP)} />
                            )}
                            {!parPaires && !afficherAvantApres && (
                                <p className="text-xs text-content-muted -mt-2">
                                    {ensemble
                                        ? "Prenez quelques photos d'ensemble du travail réalisé (jusqu'à 6) et légendez-les. La signature sur place est obligatoire."
                                        : "Aucune photo n'est demandée. La signature sur place est obligatoire."}
                                </p>
                            )}
                            {!parPaires && afficherAvantApres && <p className="text-xs text-content-muted -mt-2">
                                {dejaCommencee
                                    ? 'Photo avant déjà prise au démarrage de la prestation. Il ne manque plus que la photo après et la signature.'
                                    : photosRequises
                                        ? 'Photos avant/après et signature sur place obligatoires — elles figureront sur le document envoyé au client avec sa facture.'
                                        : 'Signature sur place obligatoire. Les photos avant/après sont facultatives : si vous les prenez, elles figureront sur le document envoyé au client.'}
                            </p>}
                            {!parPaires && ensemble && <PhotosEnsembleField photos={photosEnsemble} onChange={setPhotosEnsemble} obligatoire={modeP === 'ensemble'} />}
                            {!parPaires && afficherAvantApres && <ConsignesPhotoAvantApres />}
                            {!parPaires && afficherAvantApres && <div className="grid grid-cols-2 gap-3">
                                {dejaCommencee ? (
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Photo avant</label>
                                        <div className="rounded-xl overflow-hidden border border-border">
                                            <img src={photoBefore!} alt="Photo avant" className="w-full h-32 object-cover" />
                                        </div>
                                    </div>
                                ) : (
                                    <PhotoCaptureField label="Photo avant" value={photoBefore} onChange={setPhotoBefore} />
                                )}
                                <PhotoCaptureField label="Photo après" value={photoAfter} onChange={setPhotoAfter} />
                            </div>}
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Commentaire (optionnel)</label>
                                <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
                                    placeholder="Ex: canapé tissu, taches anciennes bien parties..."
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500 resize-none" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Nom de la personne qui reçoit la prestation</label>
                                <input type="text" value={signataireNom} onChange={(e) => setSignataireNom(e.target.value)}
                                    placeholder="Ex: Julie Martin"
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Signature de la personne qui reçoit la prestation</label>
                                <SignaturePad ref={signaturePadRef} />
                            </div>
                            {step0Error && <p className="text-xs text-red-400 font-semibold">{step0Error}</p>}
                        </>
                    ) : step === 1 ? (
                        <>
                            <p className="text-sm text-content font-semibold">Y a-t-il eu des extras pendant le rendez-vous ?</p>
                            <div className="flex gap-2">
                                <button onClick={() => setHasExtras(false)} className={pillClass(hasExtras === false)}>Non</button>
                                <button onClick={() => setHasExtras(true)} className={pillClass(hasExtras === true)}>Oui</button>
                            </div>
                            {hasExtras && (
                                <div className="space-y-3">
                                    {catalog.length > 0 && (
                                        <select
                                            onChange={(e) => { const item = catalog.find((c) => c.id === e.target.value); if (item) addCatalogExtra(item); e.target.value = ''; }}
                                            defaultValue=""
                                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500"
                                        >
                                            <option value="">+ Ajouter depuis le catalogue...</option>
                                            {catalog.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                                        </select>
                                    )}
                                    <button type="button" onClick={addBlankExtra} className="text-xs font-bold text-brand-600 dark:text-brand-400 flex items-center gap-1.5">
                                        <Plus size={14} /> Saisie libre d'une ligne
                                    </button>
                                    {extras.map((ex, idx) => (
                                        <div key={idx} className="bg-surface-soft border border-border rounded-xl p-2 space-y-1.5">
                                            <div className="flex items-center gap-2">
                                                <input value={ex.label} onChange={(e) => updateExtra(idx, { label: e.target.value })} placeholder="Description"
                                                    className="flex-1 min-w-0 bg-transparent text-sm text-content outline-none" />
                                                <input type="number" min={1} step="1" value={ex.quantity}
                                                    onChange={(e) => updateExtra(idx, { quantity: Math.max(1, Number(e.target.value || 1)) })}
                                                    placeholder="Qté" title="Quantité"
                                                    className="w-12 bg-transparent text-sm text-content outline-none text-right" />
                                                <input type="number" min={0} step="0.01" value={ex.unit_price_ttc}
                                                    onChange={(e) => updateExtra(idx, { unit_price_ttc: Number(e.target.value || 0) })}
                                                    placeholder="TTC" title="Montant TTC unitaire"
                                                    className="w-16 bg-transparent text-sm text-content outline-none text-right" />
                                                <select value={ex.tax_rate} onChange={(e) => updateExtra(idx, { tax_rate: Number(e.target.value) })}
                                                    title="TVA" className="shrink-0 bg-transparent text-xs text-content-muted outline-none">
                                                    {(tvaRates.includes(ex.tax_rate) ? tvaRates : [...tvaRates, ex.tax_rate]).map((r) => (
                                                        <option key={r} value={r}>{r}%</option>
                                                    ))}
                                                </select>
                                                <button type="button" onClick={() => removeExtra(idx)} className="text-red-400 p-1 shrink-0"><Trash2 size={14} /></button>
                                            </div>
                                            {ex.formula_note && (
                                                <p className={`text-[11px] font-semibold ${ex.unit_price_ttc === 0 ? 'text-emerald-400' : 'text-amber-500'}`}>{ex.formula_note}</p>
                                            )}
                                            <RemiseExtraField extra={ex} onChange={(patch) => updateExtra(idx, patch)} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : step === 2 ? (
                        <>
                            <p className="text-sm text-content font-semibold">Qu'est-ce qui a été fait ?</p>
                            <p className="text-xs text-content-muted -mt-2">
                                Purement informatif, le montant facturé ne change pas ici. Si un imprévu a empêché
                                de tout terminer, notez la quantité réellement faite — l'établissement pourra
                                planifier un rendez-vous pour finir.
                            </p>
                            <div className="space-y-2">
                                {lignesPrevues.map((ligne, idx) => (
                                    <div key={idx} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-surface-soft border border-border">
                                        <span className="text-sm text-content flex-1 min-w-0 truncate">{ligne.label}</span>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <input type="number" min={0} max={ligne.quantityPlanned} value={quantitesFaites[idx]}
                                                onChange={(e) => {
                                                    const v = Math.max(0, Math.min(ligne.quantityPlanned, Math.round(Number(e.target.value) || 0)));
                                                    setQuantitesFaites((prev) => prev.map((x, i) => (i === idx ? v : x)));
                                                }}
                                                className={`w-14 bg-surface border rounded-lg px-2 py-1.5 text-sm text-right outline-none focus:border-brand-500 ${quantitesFaites[idx] < ligne.quantityPlanned ? 'border-amber-500/50 text-amber-500 font-bold' : 'border-border text-content'}`} />
                                            <span className="text-xs text-content-muted">/ {ligne.quantityPlanned}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-content font-semibold">La prestation a-t-elle été payée ?</p>
                            <div className="flex gap-2">
                                <button onClick={() => setPaymentStatus('encaisse')} className={pillClass(paymentStatus === 'encaisse')}>ENCAISSER</button>
                                <button onClick={() => setPaymentStatus('a_encaisser')} className={pillClass(paymentStatus === 'a_encaisser')}>À ENCAISSER</button>
                            </div>
                            {alreadyPaidOnline > 0 && (
                                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-between text-sm">
                                    <span className="text-emerald-400 font-semibold">Déjà réglé en ligne</span>
                                    <span className="font-bold text-emerald-400">{fmt(alreadyPaidOnline)}</span>
                                </div>
                            )}
                            {paymentStatus === 'a_encaisser' && remainingAfterOnline > 0 && (
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Montant supplémentaire encaissé aujourd'hui (optionnel)</label>
                                    <input type="number" min={0} step="0.01" value={newPaymentAmount}
                                        onChange={(e) => setNewPaymentAmount(e.target.value)}
                                        placeholder="Ex: 50"
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                </div>
                            )}
                            {paymentMethodRequired && (
                                <div className="space-y-2">
                                    <p className="text-xs font-bold text-content-muted uppercase tracking-widest">Moyen de paiement (pour le montant encaissé aujourd'hui)</p>
                                    {paymentEntries.map((entry, idx) => (
                                        <div key={idx} className="space-y-1.5 p-2 rounded-xl bg-surface-soft border border-border">
                                            <div className="flex gap-2">
                                                {['Espèces', 'Virement', 'Carte bleue'].map((m) => (
                                                    <button key={m} onClick={() => updatePaymentEntry(idx, { method: m })}
                                                        className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${entry.method === m ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                                        {m}
                                                    </button>
                                                ))}
                                            </div>
                                            {paymentEntries.length > 1 && (
                                                <div className="flex items-center gap-2">
                                                    <input type="number" min={0} step="0.01" value={entry.amount}
                                                        onChange={(e) => updatePaymentEntry(idx, { amount: Number(e.target.value || 0) })}
                                                        placeholder="Montant" title="Montant pour ce moyen de paiement"
                                                        className="flex-1 bg-surface border border-border rounded-lg px-2 py-1.5 text-sm text-content outline-none focus:border-brand-500" />
                                                    <button type="button" onClick={() => removePaymentEntry(idx)} className="text-red-400 p-1 shrink-0"><Trash2 size={14} /></button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <button type="button" onClick={addPaymentEntry} className="text-xs font-bold text-brand-600 dark:text-brand-400 flex items-center gap-1.5">
                                        <Plus size={14} /> Ajouter un autre moyen de paiement
                                    </button>
                                    {paymentEntries.length > 1 && Math.abs(paymentEntriesSum - amountToAllocate) >= 0.01 && (
                                        <p className="text-xs text-red-400 font-semibold">
                                            La somme des moyens de paiement ({fmt(paymentEntriesSum)}) ne correspond pas au montant à régler ({fmt(amountToAllocate)}).
                                        </p>
                                    )}
                                </div>
                            )}
                            <div className="p-4 rounded-xl bg-surface-soft border border-border space-y-1.5 text-sm">
                                <div className="flex justify-between text-content-muted">
                                    <span>{appt.prestation_label}{appt.quantity > 1 ? ` × ${appt.quantity}` : ''}</span>
                                    <span>{fmt(baseTotal)}</span>
                                </div>
                                {validExtras.map((e, i) => (
                                    <React.Fragment key={i}>
                                        <div className="flex justify-between text-content-muted">
                                            <span>{e.label}{e.quantity > 1 ? ` × ${e.quantity}` : ''}</span>
                                            <span>{fmt(e.unit_price_ttc * e.quantity)}</span>
                                        </div>
                                        {lignesRemise(e).map((r, k) => (
                                            <div key={k} className="flex justify-between text-emerald-500">
                                                <span>{r.label}</span>
                                                <span>−{fmt(r.montant)}</span>
                                            </div>
                                        ))}
                                    </React.Fragment>
                                ))}
                                {paymentStatus === 'a_encaisser' && alreadyPaidNum > 0 && (
                                    <div className="flex justify-between text-content-muted">
                                        <span>Déjà réglé</span>
                                        <span>- {fmt(alreadyPaidNum)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between pt-2 border-t border-border font-bold text-content">
                                    <span>{paymentStatus === 'encaisse' ? 'TOTAL ENCAISSÉ' : 'TOTAL NET À PAYER'}</span>
                                    <span className={paymentStatus === 'encaisse' ? 'text-emerald-400' : 'text-red-400'}>{fmt(paymentStatus === 'encaisse' ? grandTotal : remainingDue)}</span>
                                </div>
                            </div>
                        </>
                    )}
                    {error && <p className="text-xs text-red-400 font-semibold">{error}</p>}
                </div>
                <div className="flex justify-between items-center p-5 border-t border-border shrink-0">
                    <button onClick={step === 0 ? onClose : () => setStep((s) => (s - 1) as 0 | 1 | 2 | 3)} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-sm font-bold">
                        {step === 0 ? 'Annuler' : 'Retour'}
                    </button>
                    {step === 0 ? (
                        <button onClick={async () => {
                            if (bloque) { setStep0Error(`Seul(e) ${politique?.responsable_nom || 'la personne désignée'} peut terminer cette prestation.`); return; }
                            if (manquePhotos) { setStep0Error(messagePhotos); return; }
                            if (parPaires && pairesObligatoires(modeP)) {
                                const manque = await verifierPaires(api, appt.id, 'terminer');
                                if (manque) { setStep0Error(manque); return; }
                            }
                            if (!signataireNom.trim()) { setStep0Error('Le nom de la personne qui signe est obligatoire.'); return; }
                            const sig = signaturePadRef.current?.toDataUrl();
                            if (!sig) { setStep0Error('La signature est obligatoire.'); return; }
                            setClientSignature(sig);
                            setStep0Error('');
                            // Sans accès aux prix : pas d'extras ni d'encaissement, on valide directement.
                            if (appt.prices_hidden) { handleValidate(sig); return; }
                            setStep(1);
                        }} disabled={submitting} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">{appt.prices_hidden ? (submitting ? 'Validation...' : 'Valider') : 'Suivant'}</button>
                    ) : step === 1 ? (
                        <button onClick={() => setStep(2)} disabled={hasExtras === null} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">Suivant</button>
                    ) : step === 2 ? (
                        <button onClick={() => setStep(3)} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold">Suivant</button>
                    ) : (
                        <button onClick={() => handleValidate()} disabled={submitting || !paymentEntriesValid} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                            {submitting ? 'Validation...' : 'Valider'}
                        </button>
                    )}
                </div>
            </motion.div>
        </div>
    );
}

export const PrestationEmployeePlanning: React.FC = () => {
    const [date, setDate] = useState(formatDateInput(new Date()));
    const [viewMode, setViewMode] = useState<'week' | 'day'>('week');
    const [weeklyHours, setWeeklyHours] = useState<WeeklyHours>(defaultWeeklyHours());
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [weekAppointments, setWeekAppointments] = useState<Record<string, Appointment[]>>({});
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState<Appointment | null>(null);
    const [showWizard, setShowWizard] = useState(false);
    const [showStartWizard, setShowStartWizard] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [settings, emps] = await Promise.all([
                    api('/employe/prestation-planning/settings'),
                    api('/employe/collegues'),
                ]);
                if (settings?.weekly_hours) setWeeklyHours({ ...defaultWeeklyHours(), ...settings.weekly_hours });
                setEmployees((Array.isArray(emps) ? emps : []).filter((e: any) => !e.deleted_at));
            } catch (e) {
                console.error('Failed to load prestation planning setup', e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Ouvre directement un rendez-vous depuis le lien d'une notification "vous avez été assigné"
    // (2026-09-25) — ?rdv=<id> dans l'URL. Dépend de searchParams (pas juste [] au montage) : la cloche
    // navigue vers la MÊME route /planning avec une query différente, ce qui ne remonte jamais ce
    // composant — un effet [] ne se redéclencherait donc jamais sur un second clic pendant la session.
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const rdvId = searchParams.get('rdv');
        if (!rdvId) return;
        setSearchParams({}, { replace: true });
        api(`/employe/prestation-planning/appointments/${rdvId}`).then((appt: any) => {
            if (!appt || appt.error) return;
            setDate(appt.appointment_date);
            setViewMode('day');
            setDetail(appt);
        }).catch(() => {});
    }, [searchParams]);

    const loadDay = async (d: string) => {
        try {
            const rows = await api(`/employe/prestation-planning/day?date=${d}`);
            setAppointments(Array.isArray(rows) ? rows : []);
        } catch (e) {
            setAppointments([]);
        }
    };

    const weekDates = useMemo(() => {
        const d = new Date(date + 'T00:00:00');
        const day = d.getDay();
        const diffToMonday = day === 0 ? -6 : 1 - day;
        const monday = new Date(d);
        monday.setDate(d.getDate() + diffToMonday);
        const dates: string[] = [];
        for (let i = 0; i < 7; i++) {
            const dd = new Date(monday);
            dd.setDate(monday.getDate() + i);
            dates.push(formatDateInput(dd));
        }
        return dates;
    }, [date]);

    const loadWeek = async (dates: string[]) => {
        try {
            const results = await Promise.all(dates.map((d) => api(`/employe/prestation-planning/day?date=${d}`)));
            const map: Record<string, Appointment[]> = {};
            dates.forEach((d, i) => { map[d] = Array.isArray(results[i]) ? results[i] : []; });
            setWeekAppointments(map);
        } catch (e) {
            console.error('Failed to load week appointments', e);
        }
    };

    const reloadCurrentView = async () => {
        if (viewMode === 'day') await loadDay(date);
        else await loadWeek(weekDates);
    };

    useEffect(() => {
        if (viewMode === 'day') loadDay(date);
        else loadWeek(weekDates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, viewMode]);

    const shiftPeriod = (direction: 1 | -1) => {
        const deltaDays = (viewMode === 'week' ? 7 : 1) * direction;
        const d = new Date(date + 'T00:00:00');
        d.setDate(d.getDate() + deltaDays);
        setDate(formatDateInput(d));
    };

    const dayKey = dayKeyForDate(date);
    const dayHours = weeklyHours[dayKey];
    const isDayClosed = dayHours.closed;
    const openingMinutes = timeToMinutes(dayHours.opening);
    const closingMinutes = timeToMinutes(dayHours.closing);

    const weekAxis = useMemo(() => {
        const openDays = weekDates.map((d) => weeklyHours[dayKeyForDate(d)]).filter((h) => !h.closed);
        if (openDays.length === 0) return { opening: timeToMinutes('08:00'), closing: timeToMinutes('20:00') };
        const opening = Math.min(...openDays.map((h) => timeToMinutes(h.opening)));
        const closing = Math.max(...openDays.map((h) => timeToMinutes(h.closing)));
        return { opening, closing: Math.max(closing, opening + 60) };
    }, [weekDates, weeklyHours]);

    const axisOpeningMinutes = viewMode === 'day' ? openingMinutes : weekAxis.opening;
    const axisClosingMinutes = viewMode === 'day' ? closingMinutes : weekAxis.closing;
    const axisTotalMinutes = Math.max(60, axisClosingMinutes - axisOpeningMinutes);
    const axisTotalHeight = (axisTotalMinutes / 60) * PX_PER_HOUR;

    const hourMarks = useMemo(() => {
        const marks: number[] = [];
        for (let m = axisOpeningMinutes; m <= axisClosingMinutes; m += 60) marks.push(m);
        return marks;
    }, [axisOpeningMinutes, axisClosingMinutes]);

    const weekRangeLabel = () => {
        const start = new Date(weekDates[0] + 'T00:00:00');
        const end = new Date(weekDates[6] + 'T00:00:00');
        const startStr = start.toLocaleDateString('fr-FR', { day: 'numeric', month: start.getMonth() === end.getMonth() ? undefined : 'long' });
        const endStr = end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
        return `${startStr} – ${endStr}`;
    };

    const refreshDetail = (id: string) => {
        const pool = viewMode === 'day' ? appointments : Object.values(weekAppointments).flat();
        return pool.find((a) => a.id === id) || null;
    };

    const handleCompletionDone = async () => {
        setShowWizard(false);
        setDetail(null);
        await reloadCurrentView();
    };

    const handleStartDone = async () => {
        setShowStartWizard(false);
        setDetail(null);
        await reloadCurrentView();
    };

    if (loading) {
        return (
            <div className="py-20 text-center text-content-muted flex flex-col items-center gap-2">
                <Loader2 size={28} className="animate-spin" />
                <span className="text-sm">Chargement du planning...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-black text-content">Mon Planning</h1>
                    <p className="text-sm text-content-muted mt-1 capitalize">{viewMode === 'day' ? formatDateHuman(date) : weekRangeLabel()}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1 bg-surface-soft border border-border rounded-xl p-1">
                        <button onClick={() => setViewMode('week')} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'week' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>Semaine</button>
                        <button onClick={() => setViewMode('day')} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'day' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>Jour</button>
                    </div>
                    <button onClick={() => shiftPeriod(-1)} className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all"><ChevronLeft size={16} /></button>
                    <input type="date" value={viewMode === 'week' ? weekDates[0] : date} onChange={(e) => setDate(e.target.value)}
                        className="bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                    <button onClick={() => shiftPeriod(1)} className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all"><ChevronRight size={16} /></button>
                    <button onClick={() => setDate(formatDateInput(new Date()))} className="px-3 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-xs font-bold hover:bg-surface-soft transition-all">Aujourd'hui</button>
                </div>
            </header>


            {viewMode === 'day' && isDayClosed ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl bg-surface/40">
                    <Clock size={32} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Fermé le {DAY_LABELS[dayKey].toLowerCase()}.</p>
                </div>
            ) : (
                <div className="rounded-2xl border border-border bg-surface overflow-hidden">
                    <div className="overflow-x-auto">
                        <div className="flex min-w-max">
                            <div className="w-16 shrink-0 border-r border-border">
                                <div className="h-12 border-b border-border" />
                                <div className="relative" style={{ height: axisTotalHeight }}>
                                    {hourMarks.map((m) => (
                                        <div key={m} className="absolute left-0 right-0 text-[10px] text-content-muted px-1 -translate-y-1/2"
                                            style={{ top: ((m - axisOpeningMinutes) / axisTotalMinutes) * axisTotalHeight }}>
                                            {minutesToTime(m)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {(viewMode === 'day' ? [date] : weekDates).map((d) => {
                                const dHours = weeklyHours[dayKeyForDate(d)];
                                const dAppointments = viewMode === 'day' ? appointments : (weekAppointments[d] || []);
                                const { packed, laneCount } = packAppointments(dAppointments);
                                return (
                                    <div key={d} className={viewMode === 'day' ? 'flex-1 min-w-[320px] border-r border-border last:border-r-0' : 'flex-1 min-w-[150px] border-r border-border last:border-r-0'}>
                                        <div className="h-12 border-b border-border flex items-center justify-center px-2">
                                            <span className="text-xs font-bold text-content truncate">{viewMode === 'day' ? formatDateHuman(d) : dayShortLabel(d)}</span>
                                        </div>
                                        <div className="relative" style={{ height: axisTotalHeight }}>
                                            {dHours.closed ? (
                                                <div className="absolute inset-0 flex items-center justify-center text-[11px] text-content-muted italic">Fermé</div>
                                            ) : dAppointments.length === 0 ? (
                                                hourMarks.map((m) => (
                                                    <div key={m} className="absolute left-0 right-0 border-t border-border/50"
                                                        style={{ top: ((m - axisOpeningMinutes) / axisTotalMinutes) * axisTotalHeight }} />
                                                ))
                                            ) : (
                                                <>
                                                    {hourMarks.map((m) => (
                                                        <div key={m} className="absolute left-0 right-0 border-t border-border/50"
                                                            style={{ top: ((m - axisOpeningMinutes) / axisTotalMinutes) * axisTotalHeight }} />
                                                    ))}
                                                    {packed.map(({ appt, lane }) => {
                                                        const startMin = timeToMinutes(appt.start_time);
                                                        const dureeMin = Math.round(appt.computed_duration_minutes);
                                                        const endTime = minutesToTime(startMin + dureeMin);
                                                        const top = Math.max(0, ((startMin - axisOpeningMinutes) / axisTotalMinutes) * axisTotalHeight);
                                                        const minHeight = Math.max(28, (appt.computed_duration_minutes / axisTotalMinutes) * axisTotalHeight);
                                                        const width = 100 / laneCount;
                                                        const left = lane * width;
                                                        // Même affichage détaillé que le planning côté établissement (2026-09-06) : une ligne par
                                                        // prestation avec sa quantité, le personnel isolé en bas. C'est la vue que les équipes ont
                                                        // sous les yeux sur le terrain, elle doit dire quoi faire et combien de fois.
                                                        const lignes = (appt.catalog_items && appt.catalog_items.length > 0)
                                                            ? appt.catalog_items.map((it) => ({ label: it.label, quantity: Number(it.quantity || 1) }))
                                                            : [{ label: appt.prestation_label, quantity: Number(appt.quantity || 1) }];
                                                        const maxLignes = Math.max(1, Math.floor((minHeight - 34 - 15) / 12));
                                                        const lignesVisibles = lignes.length > maxLignes ? lignes.slice(0, Math.max(1, maxLignes - 1)) : lignes;
                                                        const lignesRestantes = lignes.length - lignesVisibles.length;
                                                        const salaries = employeeNamesLabel(appt, employees);
                                                        return (
                                                            <button
                                                                key={appt.id}
                                                                type="button"
                                                                onClick={() => setDetail(appt)}
                                                                className="absolute z-10 hover:z-20 rounded-lg bg-brand-600 border border-brand-700 hover:bg-brand-500 shadow-sm transition-all px-1.5 py-1 text-left"
                                                                style={{ top, minHeight, left: `${left}%`, width: `calc(${width}% - 4px)` }}
                                                            >
                                                                <div className="flex items-baseline gap-1.5">
                                                                    <p className="text-[10px] font-bold text-white leading-snug break-words min-w-0 flex-1">{contactLabel(appt)}</p>
                                                                    {!!appt.formula_occurrence_number && (
                                                                        <span className="shrink-0 text-[8px] font-black uppercase tracking-wide text-white bg-white/25 rounded px-1 py-px">
                                                                            P{appt.formula_occurrence_number}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <p className="text-[9px] text-white/70 leading-snug">
                                                                    {dureeMin > 0 ? `${appt.start_time} - ${endTime}` : `${appt.start_time} · durée à définir`}
                                                                </p>
                                                                <div className="mt-1 pt-1 border-t border-white/25 space-y-px">
                                                                    {lignesVisibles.map((l, i) => (
                                                                        <p key={i} className="text-[9px] text-white leading-[12px] flex gap-1" title={`${l.quantity} × ${l.label}`}>
                                                                            <span className="font-black shrink-0 tabular-nums">{l.quantity}×</span>
                                                                            <span className="truncate">{l.label}</span>
                                                                        </p>
                                                                    ))}
                                                                    {lignesRestantes > 0 && (
                                                                        <p className="text-[9px] italic text-white/70 leading-[12px]">+{lignesRestantes} autre{lignesRestantes > 1 ? 's' : ''}</p>
                                                                    )}
                                                                </div>
                                                                <p className="mt-1 pt-1 border-t border-white/25 text-[9px] text-white font-bold leading-snug flex items-center gap-1">
                                                                    <User size={9} className="shrink-0 opacity-80" />
                                                                    <span className="truncate" title={salaries || undefined}>{salaries || 'Aucun salarié assigné'}</span>
                                                                </p>
                                                                {appt.status === 'termine' && (
                                                                    <p className="text-[9px] font-bold text-emerald-300 leading-snug">Terminé</p>
                                                                )}
                                                                {appt.status === 'en_cours' && (
                                                                    <p className="text-[9px] font-bold text-amber-300 leading-snug">En cours</p>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            <AnimatePresence>
                {detail && !showWizard && !showStartWizard && (
                    <DetailModal appt={refreshDetail(detail.id) || detail} onClose={() => setDetail(null)}
                        onStartCompletion={() => setShowWizard(true)} onStartPrestation={() => setShowStartWizard(true)} />
                )}
                {detail && showWizard && (
                    <CompletionWizard appt={detail} onClose={() => setShowWizard(false)} onDone={handleCompletionDone} />
                )}
                {detail && showStartWizard && (
                    <StartWizard appt={detail} onClose={() => setShowStartWizard(false)} onDone={handleStartDone} />
                )}
            </AnimatePresence>
        </div>
    );
};
